import { forwardRef, useState, useCallback, useRef, useEffect } from "react";
import type { BackgroundImageState } from "./BackgroundImageControls";

interface CompositePreviewProps {
  backgroundState: BackgroundImageState;
  width: number;
  height: number;
  children: React.ReactNode;
  onStateChange?: (state: BackgroundImageState) => void;
}

type DragTarget = "background" | "overlay" | null;

const getTouchDistance = (t0: { clientX: number; clientY: number }, t1: { clientX: number; clientY: number }) => {
  const dx = t0.clientX - t1.clientX;
  const dy = t0.clientY - t1.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

const isTouchDevice = () => typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const HANDLE_SIZE_DESKTOP = 10;
const HANDLE_SIZE_TOUCH = 16;

const ResizeHandles = ({ onResizeStart, isTouch }: { onResizeStart: (e: React.MouseEvent, corner: string) => void; isTouch: boolean }) => {
  const size = isTouch ? HANDLE_SIZE_TOUCH : HANDLE_SIZE_DESKTOP;
  const corners = [
    { key: "tl", style: { top: -size / 2, left: -size / 2, cursor: "nwse-resize" } },
    { key: "tr", style: { top: -size / 2, right: -size / 2, cursor: "nesw-resize" } },
    { key: "bl", style: { bottom: -size / 2, left: -size / 2, cursor: "nesw-resize" } },
    { key: "br", style: { bottom: -size / 2, right: -size / 2, cursor: "nwse-resize" } },
  ];

  return (
    <>
      {corners.map(({ key, style }) => (
        <div
          key={key}
          onMouseDown={(e) => onResizeStart(e, key)}
          style={{
            position: "absolute",
            width: size,
            height: size,
            background: "hsl(var(--primary))",
            border: "2px solid white",
            borderRadius: isTouch ? 4 : 2,
            zIndex: 20,
            boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
            ...style,
          }}
        />
      ))}
    </>
  );
};

const CompositePreview = forwardRef<HTMLDivElement, CompositePreviewProps>(
  ({ backgroundState, width, height, children, onStateChange }, ref) => {
    const {
      backgroundUrl, backgroundOffsetX, backgroundOffsetY, backgroundScale = 1,
      overlayOffsetX, overlayOffsetY, overlayScale, overlayOpacity,
      dragMode = "overlay",
    } = backgroundState;

    const [dragTarget, setDragTarget] = useState<DragTarget>(null);
    const [isResizing, setIsResizing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [isTouch, setIsTouch] = useState(false);
    const dragStart = useRef({ x: 0, y: 0, startOffsetX: 0, startOffsetY: 0 });
    const resizeStart = useRef({ x: 0, y: 0, scale: 1, corner: "" });
    const pinchStart = useRef({ dist: 0, scale: 1 });
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setIsTouch(isTouchDevice());
    }, []);

    const getScale = useCallback(() => {
      if (!containerRef.current) return 1;
      const rect = containerRef.current.getBoundingClientRect();
      return width / rect.width;
    }, [width]);

    const markInteracted = useCallback(() => {
      if (!hasInteracted) setHasInteracted(true);
    }, [hasInteracted]);

    // --- Mouse drag handlers ---
    const handleMouseDown = useCallback((e: React.MouseEvent, target: DragTarget) => {
      e.preventDefault();
      e.stopPropagation();
      markInteracted();
      setDragTarget(target);
      const scale = getScale();
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        startOffsetX: target === "background" ? backgroundOffsetX : overlayOffsetX,
        startOffsetY: target === "background" ? backgroundOffsetY : overlayOffsetY,
      };

      const handleMouseMove = (ev: MouseEvent) => {
        const dx = (ev.clientX - dragStart.current.x) * scale;
        const dy = (ev.clientY - dragStart.current.y) * scale;
        if (onStateChange) {
          if (target === "background") {
            onStateChange({ ...backgroundState, backgroundOffsetX: dragStart.current.startOffsetX + dx, backgroundOffsetY: dragStart.current.startOffsetY + dy });
          } else if (target === "overlay") {
            onStateChange({ ...backgroundState, overlayOffsetX: dragStart.current.startOffsetX + dx, overlayOffsetY: dragStart.current.startOffsetY + dy });
          }
        }
      };

      const handleMouseUp = () => {
        setDragTarget(null);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }, [backgroundState, backgroundOffsetX, backgroundOffsetY, overlayOffsetX, overlayOffsetY, getScale, onStateChange, markInteracted]);

    // --- Resize handlers ---
    const handleResizeStart = useCallback((e: React.MouseEvent, corner: string) => {
      e.preventDefault();
      e.stopPropagation();
      markInteracted();
      setIsResizing(true);

      const currentScale = dragMode === "background" ? backgroundScale : overlayScale;
      resizeStart.current = { x: e.clientX, y: e.clientY, scale: currentScale, corner };

      const handleResizeMove = (ev: MouseEvent) => {
        const dx = ev.clientX - resizeStart.current.x;
        const dy = ev.clientY - resizeStart.current.y;
        const viewScale = getScale();

        let delta: number;
        const c = resizeStart.current.corner;
        if (c === "br") delta = (dx + dy) / 2;
        else if (c === "tl") delta = -(dx + dy) / 2;
        else if (c === "tr") delta = (dx - dy) / 2;
        else delta = (-dx + dy) / 2;

        const scaleChange = (delta * viewScale) / 200;
        const newScale = resizeStart.current.scale + scaleChange;

        if (onStateChange) {
          if (dragMode === "background") {
            onStateChange({ ...backgroundState, backgroundScale: Math.min(Math.max(newScale, 0.5), 3) });
          } else {
            onStateChange({ ...backgroundState, overlayScale: Math.min(Math.max(newScale, 0.3), 2) });
          }
        }
      };

      const handleResizeUp = () => {
        setIsResizing(false);
        window.removeEventListener("mousemove", handleResizeMove);
        window.removeEventListener("mouseup", handleResizeUp);
      };

      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeUp);
    }, [backgroundState, backgroundScale, overlayScale, dragMode, getScale, onStateChange, markInteracted]);

    // --- Touch handlers (pinch works for both overlay AND background) ---
    const handleTouchStart = useCallback((e: React.TouchEvent, target: DragTarget) => {
      e.stopPropagation();
      markInteracted();

      if (e.touches.length === 2) {
        e.preventDefault();
        const currentScale = target === "background" ? backgroundScale : overlayScale;
        pinchStart.current = { dist: getTouchDistance(e.touches[0], e.touches[1]), scale: currentScale };

        const handleTouchMove = (ev: TouchEvent) => {
          ev.preventDefault();
          if (ev.touches.length === 2 && onStateChange) {
            const currentDist = getTouchDistance(ev.touches[0], ev.touches[1]);
            const ratio = currentDist / pinchStart.current.dist;
            const newScale = pinchStart.current.scale * ratio;
            if (target === "background") {
              onStateChange({ ...backgroundState, backgroundScale: Math.min(Math.max(newScale, 0.5), 3) });
            } else {
              onStateChange({ ...backgroundState, overlayScale: Math.min(Math.max(newScale, 0.3), 2) });
            }
          }
        };
        const handleTouchEnd = () => {
          window.removeEventListener("touchmove", handleTouchMove);
          window.removeEventListener("touchend", handleTouchEnd);
        };
        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchEnd);
        return;
      }

      if (e.touches.length === 1) {
        e.preventDefault();
        setDragTarget(target);
        const touch = e.touches[0];
        const scale = getScale();
        dragStart.current = {
          x: touch.clientX,
          y: touch.clientY,
          startOffsetX: target === "background" ? backgroundOffsetX : overlayOffsetX,
          startOffsetY: target === "background" ? backgroundOffsetY : overlayOffsetY,
        };

        const handleTouchMove = (ev: TouchEvent) => {
          ev.preventDefault();
          if (ev.touches.length !== 1) return;
          const t = ev.touches[0];
          const dx = (t.clientX - dragStart.current.x) * scale;
          const dy = (t.clientY - dragStart.current.y) * scale;
          if (onStateChange) {
            if (target === "background") {
              onStateChange({ ...backgroundState, backgroundOffsetX: dragStart.current.startOffsetX + dx, backgroundOffsetY: dragStart.current.startOffsetY + dy });
            } else if (target === "overlay") {
              onStateChange({ ...backgroundState, overlayOffsetX: dragStart.current.startOffsetX + dx, overlayOffsetY: dragStart.current.startOffsetY + dy });
            }
          }
        };

        const handleTouchEnd = () => {
          setDragTarget(null);
          window.removeEventListener("touchmove", handleTouchMove);
          window.removeEventListener("touchend", handleTouchEnd);
        };

        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchEnd);
      }
    }, [backgroundState, backgroundOffsetX, backgroundOffsetY, overlayOffsetX, overlayOffsetY, overlayScale, backgroundScale, getScale, onStateChange, markInteracted]);

    const hasBackground = !!backgroundUrl;

    // On touch devices, always show handles; on desktop, show on hover/resize
    const showHandles = isTouch || isHovered || isResizing;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <div
          ref={(node) => {
            (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            width,
            height,
            position: "relative",
            overflow: "hidden",
            touchAction: "none",
          }}
        >
          {/* Background image layer */}
          {hasBackground && (
            <div
              onMouseDown={(e) => handleMouseDown(e, "background")}
              onTouchStart={(e) => handleTouchStart(e, "background")}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                overflow: "hidden",
                cursor: dragTarget === "background" ? "grabbing" : "grab",
                userSelect: "none",
                pointerEvents: dragMode === "background" ? "auto" : "none",
                outline: showHandles && dragMode === "background" ? "2px dashed hsl(var(--primary) / 0.5)" : "none",
                outlineOffset: "-2px",
              }}
            >
              <img
                src={backgroundUrl}
                alt=""
                draggable={false}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  minWidth: "100%",
                  minHeight: "100%",
                  transform: `translate(calc(-50% + ${backgroundOffsetX}px), calc(-50% + ${backgroundOffsetY}px)) scale(${backgroundScale})`,
                  transformOrigin: "center center",
                  pointerEvents: "none",
                }}
              />
              {showHandles && dragMode === "background" && (
                <ResizeHandles onResizeStart={handleResizeStart} isTouch={isTouch} />
              )}
            </div>
          )}

          {/* Overlay layer */}
          <div
            onMouseDown={(e) => handleMouseDown(e, "overlay")}
            onTouchStart={(e) => handleTouchStart(e, "overlay")}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: dragTarget === "overlay" ? "grabbing" : "grab",
              userSelect: "none",
              pointerEvents: (!hasBackground || dragMode === "overlay") ? "auto" : "none",
            }}
          >
            <div
              style={{
                position: "relative",
                opacity: overlayOpacity,
                transform: `translate(${overlayOffsetX}px, ${overlayOffsetY}px) scale(${overlayScale})`,
                transformOrigin: "center center",
                outline: showHandles && dragMode === "overlay" ? "2px dashed hsl(var(--primary) / 0.5)" : "none",
                outlineOffset: 4,
              }}
            >
              {children}
              {showHandles && dragMode === "overlay" && (
                <ResizeHandles onResizeStart={handleResizeStart} isTouch={isTouch} />
              )}
            </div>
          </div>
        </div>

        {/* Instruction hint — disappears after first interaction */}
        {!hasInteracted && (
          <p style={{
            fontSize: 11,
            color: "hsl(var(--muted-foreground))",
            textAlign: "center",
            margin: 0,
            opacity: 0.8,
          }}>
            {isTouch
              ? "Drag to move • Pinch or drag corners to resize"
              : "Drag to move • Drag corners to resize"}
          </p>
        )}
      </div>
    );
  }
);

CompositePreview.displayName = "CompositePreview";

export default CompositePreview;
